import { useState, useCallback } from "react";
import { API } from "aws-amplify";

/**
 * S3 upload response
 */
export interface S3UploadResponse {
    s3Key?: string;
    fileName?: string;
    [key: string]: any;
}

/**
 * S3 retrieve response
 */
export interface S3RetrieveResponse {
    rxPayload?: string | any;
    [key: string]: any;
}

/**
 * Hook to manage S3 operations for prescription files
 *
 * Handles:
 * - Uploading prescription data to S3
 * - Retrieving prescription data from S3
 * - Loading and error states
 */
export const useS3Operations = () => {
    const [isUploading, setIsUploading] = useState(false);
    const [isRetrieving, setIsRetrieving] = useState(false);
    const [uploadResponse, setUploadResponse] = useState<S3UploadResponse | null>(null);
    const [retrievedData, setRetrievedData] = useState<S3RetrieveResponse | null>(null);
    const [error, setError] = useState<string>("");

    /**
     * Upload prescription data to S3
     */
    const uploadToS3 = useCallback(async (prescriptionData: any, fileName?: string): Promise<S3UploadResponse> => {
        setIsUploading(true);
        setError("");

        try {
            const apiName = "ApaqPortalAPI";
            const path = "/prescription/ingest-rx-file-to-s3-bucket";

            const payload = {
                rxPayload: JSON.stringify(prescriptionData),
                ...(fileName && { fileName }),
            };

            const response = await API.post(apiName, path, { body: payload });

            setUploadResponse(response);
            setIsUploading(false);
            return response;
        } catch (err) {
            const error = err as any;
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "Failed to upload prescription to S3";

            setError(errorMessage);
            setIsUploading(false);
            throw new Error(errorMessage);
        }
    }, []);

    /**
     * Retrieve prescription data from S3
     */
    const retrieveFromS3 = useCallback(async (fileName: string): Promise<S3RetrieveResponse> => {
        setIsRetrieving(true);
        setError("");

        try {
            const apiName = "ApaqPortalAPI";
            const path = "/prescription/retrieve-rx-file-from-s3-bucket";
            const queryParams = {
                fileName: fileName.trim(),
            };

            const response = await API.get(apiName, path, { queryStringParameters: queryParams });

            setRetrievedData(response);
            setIsRetrieving(false);
            return response;
        } catch (err) {
            const error = err as any;
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "Failed to retrieve prescription from S3";


            setError(errorMessage);
            setIsRetrieving(false);
            throw new Error(errorMessage);
        }
    }, []);

    /**
     * Reset the hook state
     */
    const reset = useCallback(() => {
        setIsUploading(false);
        setIsRetrieving(false);
        setUploadResponse(null);
        setRetrievedData(null);
        setError("");
    }, []);

    return {
        isUploading,
        isRetrieving,
        uploadResponse,
        retrievedData,
        error,
        uploadToS3,
        retrieveFromS3,
        reset,
    };
};
